/**
 * Bake an edit session into plain gaussian columns ready for any writer.
 *
 * - Deleted splats are dropped; hidden ("locked") splats are kept.
 * - Each splat's transform (output transform × palette entry) is baked into
 *   position, rotation, scale and SH. Similarity transforms take a fast
 *   quaternion path; anything with non-uniform scale re-decomposes the
 *   transformed covariance so the saved gaussian matches what was rendered.
 * - Colour grades are baked into DC (with offset), SH (linear part) and opacity.
 * - Optional export filters: selected only, minimum opacity, drop NaN/Inf rows,
 *   SH band truncation.
 */
import type { SplatEditModel } from './SplatEditModel';
import { CORE_PROPS, SH_COEFFS_PER_CHANNEL, SH_C0 } from './SplatEditModel';
import { SplatState } from './SplatState';
import { ColorGrade, createGradeTerms } from './ColorGrade';
import { SHRotation } from './SHRotation';
import { mul4 } from './ops';

export interface BakeOptions {
    /** Column-major matrix applied after each splat's palette transform. Default identity. */
    outputTransform?: ArrayLike<number>;
    selectedOnly?: boolean;
    /** Drop splats whose final opacity (0..1) is below this. */
    minOpacity?: number;
    /** Drop splats with any non-finite value. */
    removeInvalid?: boolean;
    /** Keep at most this many SH bands (0-3). */
    maxSHBands?: number;
    /** Keep hidden splats (default true, as SuperSplat does). */
    includeHidden?: boolean;
    /** Only bake rows in [start, end) (e.g. an LOD environment kept separate). */
    rowRange?: [number, number];
    /** Bake every row except those in [start, end). */
    excludeRange?: [number, number];
    /** Only bake these rows (e.g. the splats a clone stamp stroke copies). */
    rows?: ArrayLike<number>;
}

export interface BakedSplats {
    numSplats: number;
    shBands: number;
    /** Columns in standard 3DGS PLY order. */
    columns: Map<string, Float32Array>;
}

/** Column order used by writers: x y z f_dc_* f_rest_* opacity scale_* rot_*. */
export function plyColumnOrder(shBands: number): string[] {
    const rest = SH_COEFFS_PER_CHANNEL[shBands] * 3;
    const names = ['x', 'y', 'z', 'f_dc_0', 'f_dc_1', 'f_dc_2'];
    for (let i = 0; i < rest; i++) names.push(`f_rest_${i}`);
    names.push('opacity', 'scale_0', 'scale_1', 'scale_2', 'rot_0', 'rot_1', 'rot_2', 'rot_3');
    return names;
}

const IDENTITY = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
const sigmoid = (v: number) => 1 / (1 + Math.exp(-v));

interface PaletteBake {
    m: Float32Array;          // column-major 4x4
    identity: boolean;
    similarity: boolean;      // rotation × uniform scale
    rot: [number, number, number, number]; // (w, x, y, z) of the rotation part
    logScale: number;         // log of uniform scale (similarity only)
    lin: number[];            // 3x3 linear part, row-major
    shRot: SHRotation | null;
    shParity: boolean;        // mirrored: negate odd SH bands after rotating
}

export function bakeEdits(model: SplatEditModel, options: BakeOptions = {}): BakedSplats {
    const n = model.numSplats;
    const state = model.state;
    const includeHidden = options.includeHidden ?? true;
    const minOpacity = options.minOpacity ?? 0;
    const outBands = Math.min(model.shBands, Math.max(0, options.maxSHBands ?? model.shBands));
    const inPer = SH_COEFFS_PER_CHANNEL[model.shBands];
    const outPer = SH_COEFFS_PER_CHANNEL[outBands];

    const cols = new Map<string, Float32Array>();
    for (const name of CORE_PROPS) cols.set(name, model.col(name));
    const shIn: Float32Array[] = model.shColumnNames().map((nm) => model.col(nm));

    // per palette entry bake data
    const outT = options.outputTransform ?? IDENTITY;
    const pal = model.transformPalette;
    const bakes: PaletteBake[] = [];
    const tmp = new Float32Array(16);
    for (let k = 0; k < pal.count; k++) {
        pal.getMatrix(k, tmp);
        const m = mul4(outT, tmp, new Float32Array(16));
        bakes.push(analyse(m, outBands > 0));
    }

    // grades
    const grades: (ColorGrade | null)[] = [];
    for (let k = 0; k < model.colorPalette.count; k++) {
        grades.push(model.colorPalette.isIdentity(k) ? null : new ColorGrade(model.colorPalette.getTerms(k, createGradeTerms())));
    }

    const x = cols.get('x')!, y = cols.get('y')!, z = cols.get('z')!;
    const s0 = cols.get('scale_0')!, s1 = cols.get('scale_1')!, s2 = cols.get('scale_2')!;
    const r0 = cols.get('rot_0')!, r1 = cols.get('rot_1')!, r2 = cols.get('rot_2')!, r3 = cols.get('rot_3')!;
    const d0 = cols.get('f_dc_0')!, d1 = cols.get('f_dc_1')!, d2 = cols.get('f_dc_2')!;
    const op = cols.get('opacity')!;

    // pass 1: which rows survive
    let only: Uint8Array | null = null;
    if (options.rows) {
        only = new Uint8Array(n);
        for (let k = 0; k < options.rows.length; k++) {
            const r = options.rows[k];
            if (r >= 0 && r < n) only[r] = 1;
        }
    }
    const keep = new Uint8Array(n);
    let count = 0;
    for (let i = 0; i < n; i++) {
        const s = state[i];
        if (s & SplatState.deleted) continue;
        if (only && !only[i]) continue;
        if (options.rowRange && (i < options.rowRange[0] || i >= options.rowRange[1])) continue;
        if (options.excludeRange && i >= options.excludeRange[0] && i < options.excludeRange[1]) continue;
        if (!includeHidden && (s & SplatState.hidden)) continue;
        if (options.selectedOnly && !(s & SplatState.selected)) continue;
        if (minOpacity > 0) {
            const g = grades[model.colorIndex[i]];
            const a = g ? g.applyAlpha(op[i]) : sigmoid(op[i]);
            if (a < minOpacity) continue;
        }
        if (options.removeInvalid && !rowFinite(i, cols, shIn)) continue;
        keep[i] = 1;
        count++;
    }

    const names = plyColumnOrder(outBands);
    const out = new Map<string, Float32Array>();
    for (const nm of names) out.set(nm, new Float32Array(count));
    const ox = out.get('x')!, oy = out.get('y')!, oz = out.get('z')!;
    const os0 = out.get('scale_0')!, os1 = out.get('scale_1')!, os2 = out.get('scale_2')!;
    const or0 = out.get('rot_0')!, or1 = out.get('rot_1')!, or2 = out.get('rot_2')!, or3 = out.get('rot_3')!;
    const od0 = out.get('f_dc_0')!, od1 = out.get('f_dc_1')!, od2 = out.get('f_dc_2')!;
    const oop = out.get('opacity')!;
    const shOut: Float32Array[] = [];
    for (let i = 0; i < outPer * 3; i++) shOut.push(out.get(`f_rest_${i}`)!);

    const shR = new Float32Array(15), shG = new Float32Array(15), shB = new Float32Array(15);
    const rgb = { r: 0, g: 0, b: 0 };
    let o = 0;
    for (let i = 0; i < n; i++) {
        if (!keep[i]) continue;
        const b = bakes[model.transformIndex[i]];
        const px = x[i], py = y[i], pz = z[i];
        const m = b.m;
        ox[o] = m[0] * px + m[4] * py + m[8] * pz + m[12];
        oy[o] = m[1] * px + m[5] * py + m[9] * pz + m[13];
        oz[o] = m[2] * px + m[6] * py + m[10] * pz + m[14];

        if (b.identity) {
            os0[o] = s0[i]; os1[o] = s1[i]; os2[o] = s2[i];
            or0[o] = r0[i]; or1[o] = r1[i]; or2[o] = r2[i]; or3[o] = r3[i];
        } else if (b.similarity) {
            // q' = R * q, scale' = scale + log(uniform)
            const [aw, ax, ay, az] = b.rot;
            const bw = r0[i], bx = r1[i], by = r2[i], bz = r3[i];
            const qw = aw * bw - ax * bx - ay * by - az * bz;
            const qx = aw * bx + ax * bw + ay * bz - az * by;
            const qy = aw * by - ax * bz + ay * bw + az * bx;
            const qz = aw * bz + ax * by - ay * bx + az * bw;
            const ql = Math.hypot(qw, qx, qy, qz) || 1;
            or0[o] = qw / ql; or1[o] = qx / ql; or2[o] = qy / ql; or3[o] = qz / ql;
            os0[o] = s0[i] + b.logScale; os1[o] = s1[i] + b.logScale; os2[o] = s2[i] + b.logScale;
        } else {
            const res = transformCovariance(b.lin, r0[i], r1[i], r2[i], r3[i], s0[i], s1[i], s2[i]);
            or0[o] = res[0]; or1[o] = res[1]; or2[o] = res[2]; or3[o] = res[3];
            os0[o] = res[4]; os1[o] = res[5]; os2[o] = res[6];
        }

        // colour + SH
        const grade = grades[model.colorIndex[i]];
        if (grade) {
            rgb.r = d0[i] * SH_C0 + 0.5; rgb.g = d1[i] * SH_C0 + 0.5; rgb.b = d2[i] * SH_C0 + 0.5;
            grade.applyDC(rgb);
            od0[o] = (rgb.r - 0.5) / SH_C0; od1[o] = (rgb.g - 0.5) / SH_C0; od2[o] = (rgb.b - 0.5) / SH_C0;
            oop[o] = grade.applyOpacity(op[i]);
        } else {
            od0[o] = d0[i]; od1[o] = d1[i]; od2[o] = d2[i];
            oop[o] = op[i];
        }

        if (outPer > 0) {
            for (let k = 0; k < outPer; k++) {
                shR[k] = shIn[k][i];
                shG[k] = shIn[inPer + k][i];
                shB[k] = shIn[2 * inPer + k][i];
            }
            if (b.shRot) {
                const r = shR.subarray(0, outPer), g = shG.subarray(0, outPer), bl = shB.subarray(0, outPer);
                b.shRot.apply(r); b.shRot.apply(g); b.shRot.apply(bl);
            }
            if (b.shParity) {
                // inversion (-I) multiplies band l by (-1)^l: bands 1 (k 0..2) and 3 (k 8..14) flip
                for (let k = 0; k < outPer; k++) {
                    if (k >= 3 && k < 8) continue;
                    shR[k] = -shR[k]; shG[k] = -shG[k]; shB[k] = -shB[k];
                }
            }
            if (grade) {
                for (let k = 0; k < outPer; k++) {
                    rgb.r = shR[k]; rgb.g = shG[k]; rgb.b = shB[k];
                    grade.applySH(rgb);
                    shR[k] = rgb.r; shG[k] = rgb.g; shB[k] = rgb.b;
                }
            }
            for (let k = 0; k < outPer; k++) {
                shOut[k][o] = shR[k];
                shOut[outPer + k][o] = shG[k];
                shOut[2 * outPer + k][o] = shB[k];
            }
        }
        o++;
    }

    return { numSplats: count, shBands: outBands, columns: out };
}

function rowFinite(i: number, cols: Map<string, Float32Array>, sh: Float32Array[]): boolean {
    for (const c of cols.values()) if (!Number.isFinite(c[i])) return false;
    for (const c of sh) if (!Number.isFinite(c[i])) return false;
    return true;
}

function analyse(m: Float32Array, needSH: boolean): PaletteBake {
    const identity = isIdentity(m);
    const lin = [m[0], m[4], m[8], m[1], m[5], m[9], m[2], m[6], m[10]]; // row-major
    const c0 = Math.hypot(m[0], m[1], m[2]);
    const c1 = Math.hypot(m[4], m[5], m[6]);
    const c2 = Math.hypot(m[8], m[9], m[10]);
    const avg = (c0 + c1 + c2) / 3 || 1;
    // orthogonality of the columns
    const d01 = (m[0] * m[4] + m[1] * m[5] + m[2] * m[6]) / (c0 * c1 || 1);
    const d02 = (m[0] * m[8] + m[1] * m[9] + m[2] * m[10]) / (c0 * c2 || 1);
    const d12 = (m[4] * m[8] + m[5] * m[9] + m[6] * m[10]) / (c1 * c2 || 1);
    const det = lin[0] * (lin[4] * lin[8] - lin[5] * lin[7]) - lin[1] * (lin[3] * lin[8] - lin[5] * lin[6])
        + lin[2] * (lin[3] * lin[7] - lin[4] * lin[6]);
    // mirrored transforms go through the covariance path, which handles them exactly
    const similarity = det > 0
        && Math.abs(c0 - avg) / avg < 1e-4 && Math.abs(c1 - avg) / avg < 1e-4 && Math.abs(c2 - avg) / avg < 1e-4
        && Math.abs(d01) < 1e-4 && Math.abs(d02) < 1e-4 && Math.abs(d12) < 1e-4;

    // rotation part via polar decomposition (columns normalised, then orthonormalised)
    const R = polarRotation(lin);
    const rot = quatFromRotation(R);
    let shRot: SHRotation | null = null;
    // A mirror (det < 0) is the proper rotation -R composed with inversion -I.
    // Inversion multiplies SH band l by (-1)^l, so a mirrored splat rotates its
    // SH by -R and then negates the odd bands (previously its SH was left
    // unrotated, so the saved view-dependent colour no longer matched the editor).
    const shParity = needSH && det < 0 && !identity;
    const Rsh = shParity ? R.map((v) => -v) : R;
    if (needSH && det !== 0 && !identity && !isIdentity3(Rsh)) {
        // SHRotation expects column-major Mat3 data
        const data = new Float32Array([Rsh[0], Rsh[3], Rsh[6], Rsh[1], Rsh[4], Rsh[7], Rsh[2], Rsh[5], Rsh[8]]);
        shRot = new SHRotation({ data } as never);
    }
    return { m, identity, similarity, rot, logScale: Math.log(avg), lin, shRot, shParity };
}

function isIdentity(m: ArrayLike<number>): boolean {
    for (let i = 0; i < 16; i++) if (Math.abs(m[i] - IDENTITY[i]) > 1e-7) return false;
    return true;
}

function isIdentity3(r: number[]): boolean {
    return Math.abs(r[0] - 1) < 1e-7 && Math.abs(r[4] - 1) < 1e-7 && Math.abs(r[8] - 1) < 1e-7
        && Math.abs(r[1]) < 1e-7 && Math.abs(r[2]) < 1e-7 && Math.abs(r[3]) < 1e-7
        && Math.abs(r[5]) < 1e-7 && Math.abs(r[6]) < 1e-7 && Math.abs(r[7]) < 1e-7;
}

/** Nearest rotation to a 3x3 (row-major) via iterative polar decomposition. */
export function polarRotation(a: number[]): number[] {
    let q = a.slice();
    for (let it = 0; it < 30; it++) {
        const inv = invert3(q);
        if (!inv) break;
        // Q = 0.5 * (Q + Q^-T)
        const next = new Array(9);
        let diff = 0;
        for (let r = 0; r < 3; r++) {
            for (let c = 0; c < 3; c++) {
                const v = 0.5 * (q[r * 3 + c] + inv[c * 3 + r]);
                diff += Math.abs(v - q[r * 3 + c]);
                next[r * 3 + c] = v;
            }
        }
        q = next;
        if (diff < 1e-12) break;
    }
    return q;
}

function invert3(m: number[]): number[] | null {
    const [a, b, c, d, e, f, g, h, k] = m;
    const A = e * k - f * h, B = -(d * k - f * g), C = d * h - e * g;
    const det = a * A + b * B + c * C;
    if (Math.abs(det) < 1e-30) return null;
    return [
        A / det, -(b * k - c * h) / det, (b * f - c * e) / det,
        B / det, (a * k - c * g) / det, -(a * f - c * d) / det,
        C / det, -(a * h - b * g) / det, (a * e - b * d) / det,
    ];
}

/** Quaternion (w, x, y, z) from a row-major rotation matrix. */
export function quatFromRotation(m: number[]): [number, number, number, number] {
    const m00 = m[0], m01 = m[1], m02 = m[2], m10 = m[3], m11 = m[4], m12 = m[5], m20 = m[6], m21 = m[7], m22 = m[8];
    const tr = m00 + m11 + m22;
    let w: number, x: number, y: number, z: number;
    if (tr > 0) {
        const s = Math.sqrt(tr + 1) * 2;
        w = 0.25 * s; x = (m21 - m12) / s; y = (m02 - m20) / s; z = (m10 - m01) / s;
    } else if (m00 > m11 && m00 > m22) {
        const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
        w = (m21 - m12) / s; x = 0.25 * s; y = (m01 + m10) / s; z = (m02 + m20) / s;
    } else if (m11 > m22) {
        const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
        w = (m02 - m20) / s; x = (m01 + m10) / s; y = 0.25 * s; z = (m12 + m21) / s;
    } else {
        const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
        w = (m10 - m01) / s; x = (m02 + m20) / s; y = (m12 + m21) / s; z = 0.25 * s;
    }
    const l = Math.hypot(w, x, y, z) || 1;
    return [w / l, x / l, y / l, z / l];
}

/**
 * Σ' = A R S² Rᵀ Aᵀ, then eigen-decompose back to (quat, log scales).
 * Returns [w, x, y, z, ls0, ls1, ls2].
 */
export function transformCovariance(
    A: number[], qw: number, qx: number, qy: number, qz: number, ls0: number, ls1: number, ls2: number,
): number[] {
    const ql = Math.hypot(qw, qx, qy, qz) || 1;
    const w = qw / ql, x = qx / ql, y = qy / ql, z = qz / ql;
    const R = [
        1 - 2 * (y * y + z * z), 2 * (x * y - w * z), 2 * (x * z + w * y),
        2 * (x * y + w * z), 1 - 2 * (x * x + z * z), 2 * (y * z - w * x),
        2 * (x * z - w * y), 2 * (y * z + w * x), 1 - 2 * (x * x + y * y),
    ];
    const sx = Math.exp(ls0), sy = Math.exp(ls1), sz = Math.exp(ls2);
    // M = A R S
    const AR = mul3(A, R);
    const M = [AR[0] * sx, AR[1] * sy, AR[2] * sz, AR[3] * sx, AR[4] * sy, AR[5] * sz, AR[6] * sx, AR[7] * sy, AR[8] * sz];
    // Σ = M Mᵀ
    const S = new Array(9);
    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
            S[r * 3 + c] = M[r * 3] * M[c * 3] + M[r * 3 + 1] * M[c * 3 + 1] + M[r * 3 + 2] * M[c * 3 + 2];
        }
    }
    const { values, vectors } = jacobiEigen(S);
    // vectors columns are eigenvectors; ensure right-handed
    const V = vectors;
    const det = V[0] * (V[4] * V[8] - V[5] * V[7]) - V[1] * (V[3] * V[8] - V[5] * V[6]) + V[2] * (V[3] * V[7] - V[4] * V[6]);
    if (det < 0) { V[2] = -V[2]; V[5] = -V[5]; V[8] = -V[8]; }
    const q = quatFromRotation(V);
    const tiny = 1e-30;
    return [q[0], q[1], q[2], q[3],
        0.5 * Math.log(Math.max(values[0], tiny)),
        0.5 * Math.log(Math.max(values[1], tiny)),
        0.5 * Math.log(Math.max(values[2], tiny))];
}

function mul3(a: number[], b: number[]): number[] {
    const r = new Array(9);
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) r[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j];
    }
    return r;
}

/** Symmetric 3x3 eigen-decomposition (Jacobi). Row-major in/out; eigenvectors in columns. */
export function jacobiEigen(sym: number[]): { values: number[]; vectors: number[] } {
    const a = sym.slice();
    const v = [1, 0, 0, 0, 1, 0, 0, 0, 1];
    for (let sweep = 0; sweep < 50; sweep++) {
        const off = Math.abs(a[1]) + Math.abs(a[2]) + Math.abs(a[5]);
        if (off < 1e-20) break;
        for (const [p, q] of [[0, 1], [0, 2], [1, 2]] as const) {
            const apq = a[p * 3 + q];
            if (Math.abs(apq) < 1e-30) continue;
            const app = a[p * 3 + p], aqq = a[q * 3 + q];
            const theta = (aqq - app) / (2 * apq);
            const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
            const c = 1 / Math.sqrt(t * t + 1), s = t * c;
            for (let k = 0; k < 3; k++) {
                const akp = a[k * 3 + p], akq = a[k * 3 + q];
                a[k * 3 + p] = c * akp - s * akq;
                a[k * 3 + q] = s * akp + c * akq;
            }
            for (let k = 0; k < 3; k++) {
                const apk = a[p * 3 + k], aqk = a[q * 3 + k];
                a[p * 3 + k] = c * apk - s * aqk;
                a[q * 3 + k] = s * apk + c * aqk;
            }
            for (let k = 0; k < 3; k++) {
                const vkp = v[k * 3 + p], vkq = v[k * 3 + q];
                v[k * 3 + p] = c * vkp - s * vkq;
                v[k * 3 + q] = s * vkp + c * vkq;
            }
        }
    }
    return { values: [a[0], a[4], a[8]], vectors: v };
}
